import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Calendar,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  message,
  theme
} from "antd";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import FilterToolbar from "../shared/FilterToolbar";
import PageHeader from "../shared/PageHeader";
import { formatDate } from "../shared/money";
import * as api from "./api";

const STATUS_COLOR = { open: "blue", overdue: "red", done: "green", skipped: "default" };
const BADGE = { open: "processing", overdue: "error", done: "success", skipped: "default" };
const TYPE_LABEL = {
  ustva: "Umsatzsteuer-Voranmeldung",
  dauerfristverlaengerung: "Dauerfristverlängerung",
  lohnsteuer_anmeldung: "Lohnsteuer-Anmeldung",
  beitragsnachweis: "Beitragsnachweis",
  sv_beitraege: "SV-Beiträge",
  deuev_jahresmeldung: "DEÜV-Jahresmeldung",
  lohnsteuerbescheinigung: "Lohnsteuerbescheinigung",
  uv_jahresmeldung: "UV-Jahresmeldung",
  soka_bau: "SOKA-BAU",
  zm: "Zusammenfassende Meldung",
  einkommensteuer_vorauszahlung: "ESt-Vorauszahlung",
  gewerbesteuer_vorauszahlung: "GewSt-Vorauszahlung",
  jahresabschluss: "Jahresabschluss",
  freistellung_expiry: "Freistellungsbescheinigung",
  kleinunternehmer_warning: "Kleinunternehmer",
  // Abnahme / Gewährleistung / Bürgschaften (utils/warranty/deadlines.js)
  acceptance_deemed: "Fiktive Abnahme",
  defect_deadline: "Mängelbeseitigung",
  warranty_inspection: "Gewährleistungsbegehung",
  warranty_claim: "Gewährleistungsmangel",
  guarantee_return: "Bürgschaftsrückgabe",
  guarantee_expiry: "Bürgschaft läuft ab",
  custom: "Eigene Frist"
};

const Deadlines = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const { token } = theme.useToken();
  const [deadlines, setDeadlines] = useState([]);
  const [today, setToday] = useState(dayjs().format("YYYY-MM-DD"));
  const [view, setView] = useState("list");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [range, setRange] = useState(null); // [from, to] iso or null = default (overdue + 90 days)
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (type) params.type = type;
      if (range) {
        params.from = range[0];
        params.to = range[1];
      }
      const res = await api.listDeadlines(params);
      setDeadlines(res.data.deadlines || []);
      if (res.data.today) setToday(res.data.today);
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setLoading(false);
    }
  }, [status, type, range]);
  useEffect(() => {
    load();
  }, [load]);

  // The calendar needs the whole visible month, not only the default window.
  const onPanelChange = value => {
    const from = value.startOf("month").subtract(7, "day").format("YYYY-MM-DD");
    const to = value.endOf("month").add(7, "day").format("YYYY-MM-DD");
    setRange([from, to]);
  };
  const switchView = next => {
    setView(next);
    if (next === "calendar" && !range) onPanelChange(dayjs());
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) message.success(ok);
      load();
    } catch (error) {
      message.error(api.errorText(error));
    }
  };
  const submitCustom = async () => {
    try {
      const values = await form.validateFields();
      await api.createDeadline({
        title: values.title,
        description: values.description,
        link: values.link,
        dueDate: values.dueDate.format("YYYY-MM-DD")
      });
      message.success(t("Deadline created"));
      setAdding(false);
      form.resetFields();
      load();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(api.errorText(error));
    }
  };

  const byDate = useMemo(() => {
    const map = new Map();
    for (const d of deadlines) {
      if (!map.has(d.dueDate)) map.set(d.dueDate, []);
      map.get(d.dueDate).push(d);
    }
    return map;
  }, [deadlines]);

  const cellRender = (value, info) => {
    if (info.type !== "date") return info.originNode;
    const items = byDate.get(value.format("YYYY-MM-DD")) || [];
    if (!items.length) return null;
    // Phones use the compact calendar: one dot per day (red = overdue); tapping the day lists it.
    if (isMobile) {
      const color = items.some(d => d.status === "overdue")
        ? token.colorError
        : items.some(d => d.status === "open")
          ? token.colorPrimary
          : token.colorTextQuaternary;
      return (
        <span
          aria-hidden
          style={{
            position: "absolute",
            bottom: 1,
            insetInlineStart: "50%",
            transform: "translateX(-50%)",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: color
          }}
        />
      );
    }
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map(d => (
          <li
            key={d._id}
            style={{
              fontSize: 11,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            <Badge status={BADGE[d.status]} text={d.title} />
          </li>
        ))}
      </ul>
    );
  };

  const actions = d => (
    <Space size="small" wrap>
      {["open", "overdue"].includes(d.status) && (
        <>
          <Button
            size="small"
            type="link"
            onClick={() => act(() => api.markDone(d._id), t("Marked as done"))}
          >
            {t("Done")}
          </Button>
          <Button size="small" type="link" onClick={() => act(() => api.markSkipped(d._id))}>
            {t("Skip")}
          </Button>
        </>
      )}
      {["done", "skipped"].includes(d.status) && (
        <Button size="small" type="link" onClick={() => act(() => api.reopenDeadline(d._id))}>
          {t("Reopen")}
        </Button>
      )}
      {d.type === "custom" && (
        <Popconfirm
          title={t("Delete this deadline?")}
          onConfirm={() => act(() => api.deleteDeadline(d._id))}
        >
          <Button size="small" type="link" danger>
            {t("Delete")}
          </Button>
        </Popconfirm>
      )}
      {d.link ? (
        <Link to={d.link}>
          <Button size="small" type="link">
            {t("Open")}
          </Button>
        </Link>
      ) : null}
    </Space>
  );

  const overdueCount = deadlines.filter(d => d.status === "overdue").length;

  return (
    <div>
      <PageHeader
        title={t("Deadlines")}
        subtitle={t(
          "Tax and payroll deadlines, shifted to the next working day of your Bundesland"
        )}
        extra={
          <>
            <Button onClick={() => setAdding(true)}>{t("Add custom deadline")}</Button>
            <Button
              onClick={() => act(() => api.regenerateDeadlines(3), t("Deadlines regenerated"))}
            >
              {t("Regenerate")}
            </Button>
          </>
        }
      />
      <FilterToolbar
        search={
          <div className="acct-actions" style={{ alignItems: "center" }}>
            <Segmented
              value={view}
              onChange={switchView}
              options={[
                { value: "list", label: t("List") },
                { value: "calendar", label: t("Calendar") }
              ]}
            />
            {overdueCount > 0 && (
              <Tag color="red">
                {overdueCount} {t("overdue")}
              </Tag>
            )}
          </div>
        }
        activeCount={[status, type, range].filter(Boolean).length}
        filters={
          <>
            <Select
              value={status}
              onChange={setStatus}
              options={[
                { value: "", label: t("All statuses") },
                ...["open", "overdue", "done", "skipped"].map(s => ({
                  value: s,
                  label: t(`deadline_${s}`)
                }))
              ]}
            />
            <Select
              value={type}
              onChange={setType}
              showSearch
              optionFilterProp="label"
              options={[
                { value: "", label: t("All types") },
                ...Object.entries(TYPE_LABEL).map(([k, v]) => ({ value: k, label: v }))
              ]}
            />
            <DatePicker.RangePicker
              value={range ? [dayjs(range[0]), dayjs(range[1])] : null}
              onChange={v =>
                setRange(v ? [v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")] : null)
              }
              format="DD.MM.YYYY"
            />
          </>
        }
      />
      {view === "calendar" ? (
        <Card size="small">
          <Calendar
            fullscreen={!isMobile}
            cellRender={cellRender}
            onPanelChange={onPanelChange}
            onSelect={(value, info) => {
              if (info?.source !== "date") return;
              setRange([value.format("YYYY-MM-DD"), value.format("YYYY-MM-DD")]);
              setView("list");
            }}
          />
        </Card>
      ) : (
        <ResponsiveTable
          size="small"
          rowKey="_id"
          loading={loading}
          dataSource={deadlines}
          pagination={{ pageSize: 25 }}
          rowClassName={d => (d.status === "overdue" ? "ant-table-row-overdue" : "")}
          columns={[
            {
              title: t("Due"),
              dataIndex: "dueDate",
              width: 110,
              render: v => (
                <span className="acct-num" style={{ fontWeight: v === today ? 600 : 400 }}>
                  {formatDate(v)}
                </span>
              ),
              sorter: (a, b) => a.dueDate.localeCompare(b.dueDate),
              defaultSortOrder: "ascend"
            },
            {
              title: t("Title"),
              dataIndex: "title",
              mobile: "title",
              render: (v, d) => (
                <div className="acct-wrap">
                  <div>{v}</div>
                  {d.description ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                      {d.description}
                    </Typography.Text>
                  ) : null}
                </div>
              )
            },
            {
              title: t("Type"),
              dataIndex: "type",
              width: 200,
              render: v => <Tag>{TYPE_LABEL[v] || v}</Tag>
            },
            { title: t("Period"), dataIndex: "period", width: 100 },
            {
              title: t("Status"),
              dataIndex: "status",
              width: 110,
              render: s => <Tag color={STATUS_COLOR[s]}>{t(`deadline_${s}`)}</Tag>
            },
            { title: "", key: "actions", render: (_, d) => actions(d) }
          ]}
        />
      )}
      <Modal
        open={adding}
        rootClassName="acct-module"
        title={t("Add custom deadline")}
        onCancel={() => setAdding(false)}
        onOk={submitCustom}
        okText={t("Save")}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label={t("Title")}
            rules={[{ required: true, message: t("Title is required") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="dueDate"
            label={t("Due")}
            rules={[{ required: true, message: t("Due date is required") }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="description" label={t("Description")}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="link" label={t("Link (optional)")}>
            <Input placeholder="/user/accounting/..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Deadlines;
